import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, Space, Tag, App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  FileTextOutlined, 
  BookOutlined, 
  FilePdfOutlined,
  DatabaseOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { formatDateBeijing } from '../utils/datetime';

const { Title, Paragraph, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const IndustryData = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataCategories, setDataCategories] = useState([
    {
      key: 'cases',
      title: '工程案例',
      icon: <FileTextOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      description: '各类工程水土保持方案案例',
      count: 0,
      color: '#1890ff',
    },
    {
      key: 'standards',
      title: '技术标准',
      icon: <BookOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      description: '国家和行业标准规范',
      count: 0,
      color: '#1890ff',
    },
    {
      key: 'documents',
      title: '规范文档',
      icon: <FilePdfOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      description: '技术文档和指导手册',
      count: 0,
      color: '#1890ff',
    },
    {
      key: 'laws',
      title: '法律法规',
      icon: <DatabaseOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      description: '相关法律法规文件',
      count: 0,
      color: '#1890ff',
    },
    {
      key: 'websites',
      title: '重点网站',
      icon: <LinkOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      description: '国内水保重点官方网站',
      count: 0,
      color: '#1890ff',
    },
  ]);
  const [recentDocuments, setRecentDocuments] = useState([]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const casesResponse = await fetch(`${API_BASE_URL}/industry/cases?page=1&page_size=1`);
      const standardsResponse = await fetch(`${API_BASE_URL}/industry/standards?page=1&page_size=1`);
      const documentsResponse = await fetch(`${API_BASE_URL}/industry/documents?page=1&page_size=1`);
      const policiesResponse = await fetch(`${API_BASE_URL}/industry/policies?page=1&page_size=1`);
      const websitesResponse = await fetch(`${API_BASE_URL}/industry/websites?page=1&page_size=1`);

      const newCategories = [...dataCategories];

      if (casesResponse.ok) {
        const casesData = await casesResponse.json();
        newCategories[0].count = casesData.total;
      }

      if (standardsResponse.ok) {
        const standardsData = await standardsResponse.json();
        newCategories[1].count = standardsData.total;
      }

      if (documentsResponse.ok) {
        const documentsData = await documentsResponse.json();
        newCategories[2].count = documentsData.total;
      }

      if (policiesResponse.ok) {
        const policiesData = await policiesResponse.json();
        newCategories[3].count = policiesData.total;
      }

      if (websitesResponse.ok) {
        const websitesData = await websitesResponse.json();
        newCategories[4].count = websitesData.total;
      }

      setDataCategories(newCategories);
    } catch (error) {
      console.error('加载统计数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentDocuments = async () => {
    try {
      const casesResponse = await fetch(`${API_BASE_URL}/industry/cases?page=1&page_size=3`);
      const standardsResponse = await fetch(`${API_BASE_URL}/industry/standards?page=1&page_size=3`);
      const documentsResponse = await fetch(`${API_BASE_URL}/industry/documents?page=1&page_size=3`);
      const policiesResponse = await fetch(`${API_BASE_URL}/industry/policies?page=1&page_size=3`);

      const documents = [];

      if (casesResponse.ok) {
        const casesData = await casesResponse.json();
        casesData.data.forEach(item => {
          documents.push({
            id: item.id,
            title: item.name,
            type: '案例',
            date: formatDateBeijing(item.created_at),
            timestamp: item.created_at,
          });
        });
      }

      if (standardsResponse.ok) {
        const standardsData = await standardsResponse.json();
        standardsData.data.forEach(item => {
          documents.push({
            id: item.id,
            title: `${item.code} ${item.name}`,
            type: '标准',
            date: formatDateBeijing(item.created_at),
            timestamp: item.created_at,
          });
        });
      }

      if (documentsResponse.ok) {
        const docsData = await documentsResponse.json();
        docsData.data.forEach(item => {
          documents.push({
            id: item.id,
            title: item.title,
            type: '规范',
            date: formatDateBeijing(item.created_at),
            timestamp: item.created_at,
          });
        });
      }

      if (policiesResponse.ok) {
        const policiesData = await policiesResponse.json();
        policiesData.data.forEach(item => {
          documents.push({
            id: item.id,
            title: item.title,
            type: '法规',
            date: formatDateBeijing(item.publish_date) || formatDateBeijing(item.created_at),
            timestamp: item.publish_date || item.created_at,
          });
        });
      }

      documents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecentDocuments(documents.slice(0, 5));
    } catch (error) {
      console.error('加载最新文档失败:', error);
    }
  };

  useEffect(() => {
    loadStats();
    loadRecentDocuments();
  }, []);

  const getDocTypeTag = (type) => {
    const colors = {
      '标准': 'blue',
      '规范': 'green',
      '案例': 'orange',
      '法规': 'purple',
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>行业数据</Title>
        <Text type="secondary">工程案例与技术标准数据库</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        {dataCategories.map((category) => (
          <Col xs={24} sm={12} md={6} key={category.key}>
            <Card
              hoverable
              loading={loading}
              style={{ 
                textAlign: 'center', 
                cursor: 'pointer', 
                height: '100%',
                transition: 'all 0.3s',
              }}
              styles={{ body: { padding: '32px 24px' } }}
              onClick={() => {
                if (category.key === 'cases') navigate('/industry/cases');
                if (category.key === 'standards') navigate('/industry/standards');
                if (category.key === 'documents') navigate('/industry/documents');
                if (category.key === 'laws') navigate('/industry/policies');
                if (category.key === 'websites') navigate('/industry/websites');
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                {category.icon}
              </div>
              <Title level={4} style={{ marginBottom: '8px' }}>{category.title}</Title>
              <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                {category.description}
              </Paragraph>
              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: `${category.color}15`, 
                borderRadius: '16px',
                color: category.color,
                fontWeight: 500,
              }}>
                {category.count} 条记录
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span>最新文档</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<SearchOutlined />}>搜索</Button>
            <Button type="primary">查看全部</Button>
          </Space>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recentDocuments.map((doc) => (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                transition: 'all 0.3s',
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '16px' }}>{doc.title}</Text>
                  {getDocTypeTag(doc.type)}
                </div>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  更新时间：{doc.date}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Space>
                  <Button type="text" icon={<EyeOutlined />} size="small">
                    预览
                  </Button>
                  <Button type="primary" icon={<DownloadOutlined />} size="small">
                    下载
                  </Button>
                </Space>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default IndustryData;
